import React, { useEffect } from 'react';

// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { updatePageLoadInteractionName } from '@atlaskit/react-ufo/interaction-metrics';
import { updatePageloadName } from '@atlaskit/react-ufo/trace-pageload';

import { EmbeddedConfluenceTrackingContext } from '@confluence/browser-metrics/entry-points/EmbeddedConfluenceTrackingContext.ts';
import { useEmbeddedViewPageFeatures } from '@confluence/embedded-confluence-allowed-features/entry-points/view-page.ts';
import { usePageNativeEmbedClient } from '@confluence/embedded-confluence-allowed-features/entry-points/usePageNativeEmbedClient.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { CoverPictureStateContainer } from '@confluence/page-cover-picture/entry-points/CoverPictureStateContainer.ts';
import type { BaseViewPageProps } from '@confluence/view-page-common/entry-points/BaseViewPageProps.ts';
import { useViewPagePersistentScrollPosition } from '@confluence/view-page-common/entry-points/useViewPagePersistentScrollPosition.ts';
import { StartViewPageLoad } from '@confluence/view-page-common/entry-points/perf.config.ts';
import { ViewPageCommon } from '@confluence/view-page-common/entry-points/ViewPageCommon.ts';
import { useEventHandlers } from '@confluence/adf-renderer/entry-points/useEventHandlers.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { maybeParseNativeEmbedParams } from '@atlassian/native-embeds-core/child-client';

import { useTrackingConfig } from '../embedded-common/useTrackingConfig.ts';
import { usePageTheme } from '../embedded-common/usePageTheme.ts';

import { HeaderContainer } from './HeaderContainer.tsx';

// In embedded view, the renderer and title components own their own 72px gutters,
// so the outer container padding is zeroed out.
// The cover-picture CSS variables are also neutralised (to 0px / 100%) so that
// the cover image remains edge-to-edge against a zero-padded container.
// The default minHeight is overridden from calc(100vh - 56px) inherited from ContentScreenComponent to
// break a circular dependency where the iframe infinitely grows.
const EMBEDDED_CONTENT_SCREEN_STYLES = {
	paddingTop: "0px",
	paddingBottom: "0px",
	paddingLeft: "0px",
	paddingRight: "0px",
	"--full-cover-image-margin": "0px",
	"--full-cover-image-width": "100%",
	minHeight: "auto",
};

type EmbeddedViewPageProps = BaseViewPageProps & {
	classicComments?: React.ComponentType<any>;
	ClassicEditorContextProviderLoader?: React.ComponentType<any>;
	showHeaderButtons?: boolean;
};

type EmbeddedViewPageInnerProps = EmbeddedViewPageProps & {
	hasCoverPicture: boolean;
};

const EmbeddedViewPageInner: React.FC<EmbeddedViewPageInnerProps> = ({
	classicComments,
	contentId,
	spaceKey,
	ClassicEditorContextProviderLoader,
	showHeaderButtons = true, // for backwards compatibility
	hasCoverPicture,
}) => {
	usePageTheme();
	const { sendChildCommand } = usePageNativeEmbedClient();

	// Rename the UFO experience from 'view_page' to 'embedded-view-page' so that
	// embedded view page loads are reported as a distinct UFO experience.
	// Both calls are needed because there are two UFO tracking systems (trace-based
	// and legacy interaction-metrics) and we need to rename in whichever is active.
	// Skip when the page is live — LivePageComponent handles renaming to
	// 'embedded-live-edit' and this effect (parent) runs after the child's,
	// so it would overwrite the live-edit name.
	const isLivePage = useIsCurrentPageLive();
	useEffect(() => {
		if (!isLivePage) {
			updatePageloadName("embedded-view-page");
			updatePageLoadInteractionName("embedded-view-page");
		}
	}, [isLivePage]);

	const {
		hasByLineContributors,
		hasByLineExtensions,
		hasComments,
		hasInlineCommentsAndHighlightActions,
		hasPageReactions,
		hasStickyHeader,
		showDelete,
		showEdit,
		showShare,
		includeNonLicensedUsersToShare,
	} = useEmbeddedViewPageFeatures();
	const customTrackingConfig = useTrackingConfig();

	const eventHandlers = useEventHandlers();

	useViewPagePersistentScrollPosition(contentId, "EP");

	const isNativeEmbed = maybeParseNativeEmbedParams(new URLSearchParams(window.location.search));

	// Send set-show-header whenever isLivePage or hasCoverPicture changes.
	// hasCoverPicture comes from CoverPictureStateContainer which is updated immediately
	// when the user adds/removes a cover picture via the toolbar — much more reactive than
	// waiting for a GraphQL contentQueryData refetch.
	// - No cover picture: hide the header (false) — it would overlap the live doc title
	// - Cover picture present: clear the override (null) so normal showHeader logic applies
	useEffect(() => {
		if (isLivePage) {
			sendChildCommand({ type: "set-show-header", show: hasCoverPicture ? null : false } as any);
		}
	}, [isLivePage, hasCoverPicture, sendChildCommand]);

	return (
		<EmbeddedConfluenceTrackingContext.Provider value>
			<StartViewPageLoad contentId={contentId} customConfig={customTrackingConfig} />
			<ViewPageCommon
				classicComments={classicComments}
				contentId={contentId}
				contentScreenStyles={EMBEDDED_CONTENT_SCREEN_STYLES}
				eventHandlers={eventHandlers}
				hasSatisfactionSurveys={false}
				hasEOPRecs={false}
				hasInlineComments={hasInlineCommentsAndHighlightActions}
				hasLabels={false}
				hasReactions={hasPageReactions}
				hasPageComments={hasComments}
				hasInlineActions={hasInlineCommentsAndHighlightActions}
				spaceKey={spaceKey}
				isEmbeddedPage
				titleAndByLineOptions={{
					hasByLineContributors,
					hasByLineExtensions,
					headingContainer: !(
						expValEquals("cc-maui-experiment", "isEnabled", true) && isNativeEmbed
					)
						? ({ children, showInlineCommentButton }) => (
								<HeaderContainer
									contentId={contentId}
									showInlineCommentButton={showHeaderButtons && showInlineCommentButton}
									hasInlineComments={hasInlineCommentsAndHighlightActions}
									hasStickyHeader={hasStickyHeader}
									spaceKey={spaceKey}
									showDelete={showHeaderButtons && showDelete}
									showEdit={showHeaderButtons && showEdit}
									showShare={showHeaderButtons && showShare}
									includeNonLicensedUsersToShare={includeNonLicensedUsersToShare}
									hasCoverPicture={hasCoverPicture}
								>
									{children}
								</HeaderContainer>
							)
						: undefined,
				}}
				ClassicEditorContextProviderLoader={ClassicEditorContextProviderLoader}
			/>
		</EmbeddedConfluenceTrackingContext.Provider>
	);
};

export const EmbeddedViewPage: React.FC<EmbeddedViewPageProps> = (props) => (
	<Subscribe to={[CoverPictureStateContainer]}>
		{(coverPictureState: CoverPictureStateContainer) => (
			<EmbeddedViewPageInner
				{...props}
				hasCoverPicture={coverPictureState.state.hasCoverPicture}
			/>
		)}
	</Subscribe>
);
